import React, { useState, useEffect } from 'react';
import { Calculator, Square } from 'lucide-react';
import type { Wall, Room } from '../types';
import { calculateRooms } from '../utils/floorPlanUtils';
import { memoizedCalculateRooms } from '../utils/performanceOptimizations';

interface EstateAreaPanelProps {
  currentWalls: Wall[];
  roomCustomLabels: Record<string, string>;
}

interface CalculationData {
  totalArea: number;
  lastCalculated: number;
  totalRooms: number;
}

const EstateAreaPanel: React.FC<EstateAreaPanelProps> = ({
  currentWalls,
  roomCustomLabels
}) => {
  const [calculations, setCalculations] = useState<CalculationData | null>(null);
  
  // Auto-calculate on component mount and when walls change
  useEffect(() => {
    const calculateMetrics = async () => {
      // Use memoized room calculation with 'explicitRoomCreation' context to get all rooms for area calculation
      const rooms = memoizedCalculateRooms(currentWalls, roomCustomLabels, 'explicitRoomCreation', undefined, undefined);
      // Calculate total area directly from all rooms
      const totalArea = rooms.reduce((sum: number, room: Room) => sum + room.area, 0);
      
      setCalculations({
        totalArea,
        lastCalculated: Date.now(),
        totalRooms: rooms.length
      });
    };

    calculateMetrics();
  }, [currentWalls, roomCustomLabels]);

  if (!calculations) {
    return (
      <div className="bg-gradient-to-br from-slate-50 via-white to-slate-50 rounded-2xl shadow-lg border border-slate-200/50 p-6 h-full flex items-center justify-center">
        <div className="flex items-center justify-center gap-3 text-slate-500">
          <div className="p-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg">
            <Calculator size={20} className="animate-spin text-white" />
          </div>
          <span className="text-base font-medium">Calculating area...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white via-slate-50 to-white rounded-2xl shadow-xl border border-slate-200/50 overflow-hidden flex flex-col h-full">
      {/* Compact Header with Gradient - Matches Element Tools */}
      <div className="relative px-2 py-1 border-b border-slate-200/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <div className="p-0.5 bg-emerald-100 rounded shadow-sm">
              <Calculator size={12} className="text-emerald-700" />
            </div>
            <h3 className="font-bold text-xs text-slate-700">Live Area</h3>
          </div>
          {/* Compact Live Indicator */}
          <div className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 rounded border border-emerald-200">
            <div className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-[10px] font-semibold text-emerald-700">LIVE</span>
          </div>
        </div>
      </div>

      {/* Compact Area Display - Horizontal Layout */}
      <div className="p-2 flex-grow flex flex-col justify-center">
        <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-100 rounded-lg p-3 lg:p-4 border border-emerald-200/50 hover:shadow-sm transition-all duration-200 h-full flex items-center justify-center">
          <div className="flex items-center justify-center gap-4 lg:gap-5 w-full">
            {/* Icon */}
            <div className="p-2.5 lg:p-3 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg shadow-md flex-shrink-0">
              <Square size={20} className="lg:w-6 lg:h-6 text-white" />
            </div>
            
            {/* Area Label and Value */}
            <div className="flex flex-col flex-1 min-w-0">
              <p className="text-xs lg:text-sm font-semibold text-emerald-600 uppercase tracking-wide leading-tight mb-1">AREA</p>
              <div className="flex items-baseline gap-1.5">
                <p className="text-3xl lg:text-4xl font-bold text-emerald-700 leading-none">
                  {calculations.totalArea.toFixed(2)}
                </p>
                <p className="text-sm lg:text-base font-medium text-emerald-600">m²</p>
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex flex-col gap-1.5 flex-shrink-0">
              <div className="flex items-center gap-1 px-2.5 py-1.5 bg-white/60 rounded text-xs lg:text-sm">
                <span className="font-bold text-emerald-700">{calculations.totalRooms}</span>
                <span className="text-emerald-600">Rooms</span>
              </div>
              <div className="flex items-center gap-1 px-2.5 py-1.5 bg-white/60 rounded text-xs lg:text-sm">
                <span className="font-bold text-emerald-700">{currentWalls.length}</span>
                <span className="text-emerald-600">Walls</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compact Footer */}
      <div className="mx-2 mb-2 px-2 py-0.5 bg-gradient-to-r from-slate-50 to-white rounded border border-slate-200/50">
        <div className="flex justify-between items-center text-[9px]">
          <div className="flex items-center gap-0.5">
            <div className="w-0.5 h-0.5 bg-emerald-500 rounded-full"></div>
            <span className="text-slate-600 font-medium">
              {new Date(calculations.lastCalculated).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </span>
          </div>
          <span className="text-slate-500">Estate</span>
        </div>
      </div>
    </div>
  );
};

export default EstateAreaPanel;
